import { ApiClient } from './api';

export interface MembershipSubscription {
  id: string;
  customerId: string;
  productId: string;
  startDate: string;
  endDate?: string;
  status: string;
  product: {
    id: string;
    name: string;
    price: number;
    billingType: string;
    installments?: number;
    monthlyDueAmount?: number;
  };
  paymentSchedules: PaymentSchedule[];
}

export interface PaymentSchedule {
  id: string;
  subscriptionId: string;
  installmentNumber: number;
  dueDate: string;
  amount: number;
  status: string;
  paidAt?: string;
  paymentMethod?: string;
  paymentLinks: PaymentLink[];
}

export interface PaymentLink {
  id: string;
  subscriptionId: string;
  paymentScheduleId: string;
  amount: number;
  expiresAt: string;
  customerEmail: string;
  customerName: string;
  url: string;
  token: string;
  usedAt?: string;
}

class MembershipService {
  private apiClient: ApiClient;

  constructor() {
    this.apiClient = new ApiClient('http://localhost:3001/api/v1/membership');
  }

  /**
   * Get active membership subscription for a customer
   */
  async getActiveMembership(customerId: string): Promise<MembershipSubscription | null> {
    try {
      console.log('🔍 Fetching active membership for customer:', customerId);
      console.log('🔍 API Client base URL:', this.apiClient);
      const endpoint = `/subscriptions/active/${customerId}`;
      console.log('🔍 Making request to endpoint:', endpoint);
      
      const response = await this.apiClient.get<{ success: boolean; data: MembershipSubscription | null }>(endpoint);
      
      console.log('🔍 Raw API response:', response);
      
      if (response && response.success && response.data) {
        console.log('✅ Found active membership:', response.data);
        return response.data;
      } else {
        console.log('ℹ️ No active membership found for customer:', customerId, 'Response:', response);
        return null;
      }
    } catch (error) {
      console.error('❌ Error fetching active membership:', error);
      console.error('❌ Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      return null;
    }
  }

  /**
   * Get all membership subscriptions for a customer
   */
  async getMemberships(customerId: string): Promise<MembershipSubscription[]> {
    try {
      console.log('🔍 Fetching all memberships for customer:', customerId);
      const response = await this.apiClient.get<{ success: boolean; data: MembershipSubscription[] }>(
        `/subscriptions?customerId=${customerId}`
      );
      
      if (response.success && response.data) {
        console.log('✅ Found memberships:', response.data);
        return response.data;
      } else {
        console.log('ℹ️ No memberships found for customer:', customerId);
        return [];
      }
    } catch (error) {
      console.error('❌ Error fetching memberships:', error);
      return [];
    }
  }

  /**
   * Get membership subscription by ID
   */
  async getMembershipById(subscriptionId: string): Promise<MembershipSubscription | null> {
    try {
      console.log('🔍 Fetching membership by ID:', subscriptionId);
      const response = await this.apiClient.get<{ success: boolean; data: MembershipSubscription | null }>(
        `/subscriptions/${subscriptionId}`
      );
      
      if (response.success && response.data) {
        console.log('✅ Found membership:', response.data);
        return response.data;
      } else {
        console.log('ℹ️ Membership not found:', subscriptionId);
        return null;
      }
    } catch (error) {
      console.error('❌ Error fetching membership by ID:', error);
      return null;
    }
  }
}

export const membershipService = new MembershipService();
